import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import ProductProp from '../../../../propTypes/ProductProp';
import ProductImage from '../../pack/ProductImage';
import Switch from '../../../../../../commonComponents/forms/inputs/Switch';
import {
  isInCart,
  PRODUCT_TYPE,
} from '../../../../../../services/utilityFunctions';
import PackProp from '../../../../propTypes/PackProp';
import {
  getPriceOfProduct,
  isFeatureAvailable,
} from '../../../../../../services/cartCalculations';
import PlanProp from '../../../../propTypes/PlanProp';
import CartProp from '../../../../propTypes/CartProp';

export default function ProductItemModalHeader({
  t,
  onClose,
  product,
  onClick,
  handleChangeQuantity,
  quantity,
  interval,
  currentUserStats,
  selectedPlan,
  cart,
  hasPermissionToChange,
}) {
  const { name, type, showBought, belongsToPack, allowQuantity } = product;
  const isInsideCart = isInCart(cart, product);
  const isSubscription =
    type === PRODUCT_TYPE.SUBSCRIPTION ||
    type === PRODUCT_TYPE.PREMIUM_SUBSCRIPTION;
  const featureAvailable = isFeatureAvailable(product, selectedPlan);
  const { price, isFlat } = getPriceOfProduct(
    product,
    currentUserStats.totalUsers,
  );
  const numberValue = price > 0 ? Number(price) / 100 : 0;
  const value = numberValue.toFixed(2);
  const formattedPrice = value ? `${value}€` : value;

  const addText = !isInsideCart && !showBought ? 'add' : 'bought';

  let inputCaption = '';
  if (isSubscription) {
    inputCaption = 'product-modal-input-caption-subscription';
  } else if (isInsideCart && !showBought) {
    inputCaption = 'product-modal-input-caption-removable';
  } else if (showBought) {
    inputCaption = 'product-modal-input-caption-bought';
  } else if (!isInsideCart && !showBought && allowQuantity) {
    inputCaption = 'product-modal-input-caption-allowQuantity';
  } else if (!isInsideCart && !showBought) {
    inputCaption = 'product-modal-input-caption-add';
  }

  let priceCaption = '';
  if (isSubscription) {
    if (isFlat) {
      priceCaption = `/${t(`interval_${interval}`)}`;
    } else {
      priceCaption =
        price > 0
          ? t('subscriptionCreate_perDistributor', t(`interval_${interval}`))
          : t('subscriptionCreate_includedOnPack');
    }
  }

  return (
    <div className="modal-header product-item-modal-header">
      <div className="modal-header-image">
        <ProductImage
          className="product-item-img"
          product={product}
          style={{ width: '100%', height: '100%', top: '0', left: '0' }}
          light
        />
      </div>
      <div>
        <div className="product-title">{name}</div>
        {!belongsToPack && (
          <div className="modal-input">
            <div className="modal-input-caption">{t(inputCaption)}</div>
            <div className="modal-input-button">
              {isSubscription ? (
                <div>
                  <Switch
                    onClick={onClick}
                    checked={isInsideCart && featureAvailable}
                    id={product._id}
                    disabledModuleItem={
                      !featureAvailable || !hasPermissionToChange
                    }
                  />
                </div>
              ) : (
                <div>
                  {!isInsideCart && !showBought && allowQuantity ? (
                    <div className="modal-input-quantity-container">
                      <div className="input-group modal-input-quantity-selector">
                        <div className="input-group-prepend modal-input-quantity-btn">
                          <button
                            className="btn btn-outline-primary"
                            style={{ padding: '5px' }}
                            type="button"
                            disabled={quantity <= 1}
                            onClick={() =>
                              handleChangeQuantity({ type: 'decrement' })
                            }
                          >
                            -
                          </button>
                        </div>
                        <input
                          className="form-control modal-input-quantity modal-input-quantity-btn"
                          type="number"
                          onChange={evt =>
                            handleChangeQuantity({
                              type: 'set',
                              value: Number(evt.currentTarget.value),
                            })
                          }
                          value={quantity}
                        />
                        <div className="input-group-append modal-input-quantity-btn">
                          <button
                            className="btn btn-outline-primary"
                            style={{ padding: '5px' }}
                            type="button"
                            onClick={() =>
                              handleChangeQuantity({ type: 'increment' })
                            }
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <div>
                        <button
                          disabled={showBought || !featureAvailable}
                          type="button"
                          className={cx(
                            'btn m-btn--pill m-btn--air btn-sm m-btn m-btn--custom modal-btn',
                            {
                              'modal-btn-available':
                                !isInsideCart && !showBought,
                            },
                            { 'modal-btn-remove': isInsideCart && !showBought },
                            { 'modal-btn-remove-no-hover': showBought },
                          )}
                          onClick={onClick}
                        >
                          <b>{isInsideCart ? t('remove') : t(addText)}</b>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <button
                        disabled={showBought || !featureAvailable}
                        type="button"
                        className={cx(
                          'btn m-btn--pill m-btn--air btn-sm m-btn m-btn--custom modal-btn',
                          {
                            'modal-btn-available': !isInsideCart && !showBought,
                          },
                          { 'modal-btn-remove': isInsideCart && !showBought },
                          { 'modal-btn-remove-no-hover': showBought },
                        )}
                        onClick={onClick}
                      >
                        <b>{isInsideCart ? t('remove') : t(addText)}</b>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="modal-price">
        <div className="price">{formattedPrice}</div>
        {priceCaption.length > 0 && (
          <div className="price-caption">{priceCaption}</div>
        )}
      </div>

      {onClose && (
        <button type="button" className="close" onClick={onClose}>
          <span aria-hidden="true">×</span>
        </button>
      )}
    </div>
  );
}

ProductItemModalHeader.propTypes = {
  t: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  product: PropTypes.oneOfType([
    PropTypes.shape(ProductProp.propType),
    PropTypes.shape(PackProp.propType),
  ]),
  currentUserStats: PropTypes.shape({
    totalUsers: PropTypes.number,
    totalAdmins: PropTypes.number,
  }).isRequired,
  onClick: PropTypes.func,
  handleChangeQuantity: PropTypes.func.isRequired,
  quantity: PropTypes.number,
  interval: PropTypes.string.isRequired,
  selectedPlan: PropTypes.shape(PlanProp.propType).isRequired,
  cart: PropTypes.shape(CartProp.propType).isRequired,
  hasPermissionToChange: PropTypes.bool,
};

ProductItemModalHeader.defaultProps = {
  onClose: null,
  product: ProductProp.defaultProp,
  onClick: null,
  quantity: 1,
  hasPermissionToChange: true,
};
